import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApp } from '@/components/AppLayout';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { X, User } from 'lucide-react';
import {
  clientApi,
  type Client,
  type CreateClientData,
  type PaymentTermsChoice,
  type Phone,
} from '@/lib/api';

interface ClientDialogProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  client?: Client | null;
  onSaved: (client?: Client) => void;
}

export function ClientDialog({ open, onClose, businessId, client, onSaved }: ClientDialogProps) {
  const { currentBusiness } = useApp();
  const isEditing = !!client;

  const [paymentTermsChoices, setPaymentTermsChoices] = useState<PaymentTermsChoice[]>([]);

  const [billingName, setBillingName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [emails, setEmails] = useState<string[]>(['']);
  const [phones, setPhones] = useState<Phone[]>([{ country_code: '+1', number: '' }]);
  const [website, setWebsite] = useState('');
  const [address, setAddress] = useState('');
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [billingAddress, setBillingAddress] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('same_day');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load choices on mount
  useEffect(() => {
    clientApi.getPaymentTerms().then((response) => {
      if (response.data) {
        setPaymentTermsChoices(response.data);
      }
    });
  }, []);

  // Populate form when editing or reset when adding
  useEffect(() => {
    if (open) {
      if (client) {
        setBillingName(client.billing_name);
        setCompanyName(client.company_name);
        setEmails(client.emails.length > 0 ? client.emails : ['']);
        setPhones(client.phones.length > 0 ? client.phones : [{ country_code: '+1', number: '' }]);
        setWebsite(client.website);
        setAddress(client.address);
        setSameAsBilling(!client.billing_address || client.billing_address === '');
        setBillingAddress(client.billing_address);
        setTaxNumber(client.tax_number);
        setPaymentTerms(client.payment_terms);
        setNotes(client.notes);
      } else {
        // Reset form for adding new client
        setBillingName('');
        setCompanyName('');
        setEmails(['']);
        setPhones([{ country_code: '+1', number: '' }]);
        setWebsite('');
        setAddress('');
        setSameAsBilling(true);
        setBillingAddress('');
        setTaxNumber('');
        setPaymentTerms('same_day');
        setNotes('');
      }
      setError('');
    }
  }, [open, client]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError('');
    setSubmitting(true);

    const data: CreateClientData = {
      billing_name: billingName,
      company_name: companyName,
      emails: emails.filter(e => e.trim() !== ''),
      phones: phones.filter(p => p.number.trim() !== ''),
      website,
      address,
      billing_address: sameAsBilling ? '' : billingAddress,
      tax_number: taxNumber,
      payment_terms: paymentTerms,
      notes,
      business: businessId,
    };

    const response = isEditing
      ? await clientApi.update(client.id, data)
      : await clientApi.create(data);

    setSubmitting(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    onSaved(response.data);
    onClose();
  };

  const addEmail = () => {
    setEmails([...emails, '']);
  };

  const removeEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const addPhone = () => {
    setPhones([...phones, { country_code: '+1', number: '' }]);
  };

  const removePhone = (index: number) => {
    setPhones(phones.filter((_, i) => i !== index));
  };

  if (!open) return null;

  return (
    <>
      {/* Full-screen overlay */}
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
            <div className="h-6 w-px bg-border" />
            <span className="font-medium text-sm">{currentBusiness?.name}</span>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col items-center justify-center px-4 py-16">
            {/* Centered content container */}
            <div className="w-full max-w-md">
              <h1 className="text-2xl mb-8 flex items-center gap-2">
                {isEditing ? 'Edit Client' : 'Add Client'}
                <User className="h-5 w-5 text-blue-400" />
              </h1>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2 group">
                  <Label htmlFor="dialog-billingName" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Billing name</Label>
                  <Input
                    id="dialog-billingName"
                    placeholder="e.g., John Doe"
                    value={billingName}
                    onChange={(e) => setBillingName(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2 group">
                  <Label htmlFor="dialog-companyName" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Company name (optional)</Label>
                  <Input
                    id="dialog-companyName"
                    placeholder="e.g., Acme Inc."
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </div>

                {/* Email Section */}
                <div className="space-y-2 group">
                  <Label className="text-muted-foreground group-focus-within:text-foreground transition-colors">Email addresses</Label>
                  <div className="space-y-2">
                    {emails.map((email, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder="email@example.com"
                          value={email}
                          onChange={(e) => {
                            const newEmails = [...emails];
                            newEmails[index] = e.target.value;
                            setEmails(newEmails);
                          }}
                        />
                        {emails.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEmail(index)}
                            className="p-2 text-muted-foreground hover:text-foreground transition-colors shrink-0"
                            aria-label="Remove email"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addEmail}
                      className="text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      + Add another email
                    </button>
                  </div>
                </div>

                {/* Phone Section */}
                <div className="space-y-2 group">
                  <Label className="text-muted-foreground group-focus-within:text-foreground transition-colors">Phone numbers</Label>
                  <div className="space-y-2">
                    {phones.map((phone, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder="+1"
                          value={phone.country_code}
                          onChange={(e) => {
                            const newPhones = [...phones];
                            newPhones[index] = { ...newPhones[index], country_code: e.target.value };
                            setPhones(newPhones);
                          }}
                          className="w-20 shrink-0"
                        />
                        <Input
                          placeholder="555-1234"
                          value={phone.number}
                          onChange={(e) => {
                            const newPhones = [...phones];
                            newPhones[index] = { ...newPhones[index], number: e.target.value };
                            setPhones(newPhones);
                          }}
                        />
                        {phones.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePhone(index)}
                            className="p-2 text-muted-foreground hover:text-foreground transition-colors shrink-0"
                            aria-label="Remove phone"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addPhone}
                      className="text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      + Add another phone
                    </button>
                  </div>
                </div>

                <hr className="border-border" />

                <div className="space-y-2 group">
                  <Label htmlFor="dialog-address" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Address</Label>
                  <AddressAutocomplete
                    id="dialog-address"
                    placeholder="Start typing an address..."
                    value={address}
                    onChange={setAddress}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="dialog-sameAsBilling"
                      checked={sameAsBilling}
                      onCheckedChange={(checked) => setSameAsBilling(checked === true)}
                    />
                    <Label htmlFor="dialog-sameAsBilling" className="font-normal cursor-pointer text-sm text-muted-foreground">
                      Billing address same as address
                    </Label>
                  </div>
                  {!sameAsBilling && (
                    <AddressAutocomplete
                      placeholder="Start typing a billing address..."
                      value={billingAddress}
                      onChange={setBillingAddress}
                      rows={2}
                    />
                  )}
                </div>

                <hr className="border-border" />

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 group">
                    <Label htmlFor="dialog-website" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Website (optional)</Label>
                    <Input
                      id="dialog-website"
                      placeholder="https://example.com"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2 group">
                    <Label htmlFor="dialog-taxNumber" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Tax number (optional)</Label>
                    <Input
                      id="dialog-taxNumber"
                      placeholder="Tax ID"
                      value={taxNumber}
                      onChange={(e) => setTaxNumber(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2 group">
                  <Label htmlFor="dialog-paymentTerms" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Payment terms</Label>
                  <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select payment terms" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentTermsChoices.map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 group">
                  <Label htmlFor="dialog-notes" className="text-muted-foreground group-focus-within:text-foreground transition-colors">Internal notes (optional)</Label>
                  <p className="text-xs text-muted-foreground">These notes are not visible to the client</p>
                  <Textarea
                    id="dialog-notes"
                    placeholder="Add any internal notes about this client..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>

                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1 rounded-full"
                  >
                    Go Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 rounded-full"
                    disabled={submitting || !billingName}
                  >
                    {submitting
                      ? isEditing ? 'Saving...' : 'Creating...'
                      : isEditing ? 'Save Changes' : 'Create Client'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
